import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import { getCrmDedDetailsEndpointIdentifier } from './use-get-crm-ded-details-api';

export interface IUpdateDedDetailPayload {
  englishName?: string;
  arabicName?: string;
  businessName?: string;
  licenseStatus?: string;
  legalType?: string;
  expiryDate?: string;
  activities?: string;
  telephone?: string;
  fax?: string;
  mobile?: string;
  poBox?: string;
  email?: string;
  address?: string;
  area?: string;
  subArea?: string;
  community?: string;
  landmark?: string;
  parcelId?: string;
  street?: string;
  building?: string;
  floor?: string;
  unit?: string;
  gpsCoordinates?: string;
  licenseNumber?: string;
}

interface IUpdateDedDetailResponse {
  id: number;
  englishName?: string;
  arabicName?: string;
  pageUrl?: string;
  businessName: string;
  licenseStatus?: string;
  legalType?: string;
  licenseNumber: string;
  telephone?: string;
  mobile?: string;
  email?: string;
  address?: string;
  area?: string;
  activities?: string;
  expiryDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface UseUpdateDedDetailApiParams {
  id: number;
  data: IUpdateDedDetailPayload;
}

const endpoint = 'ums/v1/crm/management/ded/licenses';

type TData = IUpdateDedDetailResponse;
type TError = AxiosError;
type TVariables = UseUpdateDedDetailApiParams;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useUpdateDedDetailApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, data }: TVariables): Promise<TData> => {
    const response = await http.put(`${endpoint}/${id}`, data);
    return response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: endpoint,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getCrmDedDetailsEndpointIdentifier]);
      queryClient.invalidateQueries([endpoint, data.id]);
      queryClient.invalidateQueries(['ums/v1/crm/management/ded/licenses/stats/overview']);

      if (userOnSuccess) {
        userOnSuccess(data, variables, context);
      }
    },
    ...restOptions,
  });
}
